// src/pages/PaymentSuccess.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "../services/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const freeOnly = params.get("free") === "1";

  // show diagnostic box only on localhost or with ?debug=1
  const debug = params.get("debug") === "1";
  const isLocal =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  const showDiagnostic = isLocal || debug;

  const [state, setState] = useState(freeOnly ? "FULFILLED" : "LOADING");
  const [message, setMessage] = useState(
    freeOnly
      ? "Your free ticket(s) have been issued. Check your email for the QR code."
      : "Checking your order…"
  );

  // Strip session_id from the URL once read (preserve other params like ?free=1)
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("session_id")) {
      url.searchParams.delete("session_id");
      const qs = url.searchParams.toString();
      window.history.replaceState({}, "", url.pathname + (qs ? `?${qs}` : "") + url.hash);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || freeOnly) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `/api/tickets/checkout-result?session_id=${sessionId}`
        );
        setState(data.state);
        setMessage(data.message);
      } catch {
        setState("UNKNOWN");
        setMessage(
          "We couldn't verify the checkout result. If you don't receive tickets or a refund email, please contact support."
        );
      }
    })();
  }, [sessionId, freeOnly]);

  const heading = useMemo(
    () =>
      ({
        FULFILLED: "Payment Successful",
        REFUNDED_TIMEOUT: "Order Timed Out — Refund Issued",
        TIMEOUT_REFUND_TRIGGERED: "Order Timed Out — Refund Initiated",
        UNKNOWN: "Payment Completed",
        LOADING: "Finalizing…",
      }[state]),
    [state]
  );

  const meta = useMemo(() => {
    switch (state) {
      case "FULFILLED":
        return {
          tone: "text-emerald-200",
          ring: "ring-emerald-500/30",
          badge: "badge bg-emerald-500/20 text-emerald-200",
          iconBg: "bg-emerald-500/15",
          icon: (
            <svg viewBox="0 0 24 24" className="h-7 w-7">
              <path fill="currentColor" d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
            </svg>
          ),
        };
      case "REFUNDED_TIMEOUT":
      case "TIMEOUT_REFUND_TRIGGERED":
        return {
          tone: "text-amber-200",
          ring: "ring-amber-500/30",
          badge: "badge bg-amber-500/20 text-amber-200",
          iconBg: "bg-amber-500/15",
          icon: (
            <svg viewBox="0 0 24 24" className="h-7 w-7">
              <path fill="currentColor" d="M12 1a11 11 0 1 0 11 11A11.013 11.013 0 0 0 12 1Zm1 11H7V10h5V5h2v7Z" />
            </svg>
          ),
        };
      case "LOADING":
        return {
          tone: "text-slate-200",
          ring: "ring-brand-500/30",
          badge: "badge bg-brand-600/20 text-slate-100",
          iconBg: "bg-white/10",
          icon: <div className="h-7 w-7 rounded-full border-2 border-white/30 border-t-transparent animate-spin" />,
        };
      default:
        return {
          tone: "text-slate-200",
          ring: "ring-white/20",
          badge: "badge bg-white/10 text-slate-200",
          iconBg: "bg-white/10",
          icon: (
            <svg viewBox="0 0 24 24" className="h-7 w-7">
              <path
                fill="currentColor"
                d="M11 7h2v6h-2V7zm1 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2z"
              />
            </svg>
          ),
        };
    }
  }, [state]);

  return (
    <div className="page max-w-2xl">
      <section className="card text-center">
        {/* Icon */}
        <div
          className="mx-auto mb-3 grid h-16 w-16 place-items-center rounded-full border border-white/10 shadow-card ring-2"
          style={{ boxShadow: "0 10px 25px rgba(0,0,0,.35)" }}
          data-meta={state}
        >
          <div className={`h-14 w-14 grid place-items-center rounded-full ${meta.iconBg} ${meta.tone} ${meta.ring}`}>
            {meta.icon}
          </div>
        </div>

        {/* Heading + badge */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <h1 className="font-display text-3xl md:text-4xl">{heading}</h1>
          <span className={meta.badge}>
            {state === "FULFILLED" ? "Paid" : state === "LOADING" ? "Processing" : state === "UNKNOWN" ? "Status Unknown" : "Refund"}
          </span>
        </div>

        {/* Message */}
        <p className="mt-3 text-slate-300">{message}</p>

        {/* Session info — diagnostics only */}
        {showDiagnostic && sessionId && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-xs text-slate-300">
            <span className="text-slate-400">Checkout session:</span>
            <code className="rounded bg-black/30 px-2 py-1 font-mono text-[11px] text-slate-200">{sessionId}</code>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <Link to="/events" className="btn-primary">Explore events</Link>
          <Link to="/" className="btn-secondary">Back to home</Link>
        </div>

        {/* Help text */}
        <div className="mt-5 grid gap-2 text-left text-sm text-slate-400">
          <div className="flex items-start gap-2">
            <span className="mt-[2px]">•</span>
            <span>Tickets are sent to your email. If you don’t see them, check your spam/junk folder.</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="mt-[2px]">•</span>
            <span>Keep this window open until you receive confirmation. You can safely close it afterwards.</span>
          </div>
        </div>
      </section>
    </div>
  );
}
